import { useErrorHandler } from '~/hooks/useErrorHandler';
import { ButtonLink, Flex, Heading, SrOnly, Table, Tag, Checkbox } from '@applyboard/crystal-ui';
import { DocumentAlertHifiIcon, EyeOutlineIcon } from '@applyboard/ui-icons';
import { useSearchResult, useSelectedIntakes } from '~/context';
import { Intake } from '~/data';

export const enumIntakeStatuses = [
  { code: 'OPEN', name: 'Open' },
  { code: 'WILL_OPEN', name: 'Opening Soon' },
  { code: 'CLOSED', name: 'Closed' },
];

export function IntakesTable() {
  const { apiError } = useErrorHandler();
  const { selectedIntakes, toggle } = useSelectedIntakes();
  const { intakes, isFetchingIntakes, isIntakesError } = useSearchResult();

  if (isIntakesError) {
    apiError();
    return null;
  }

  return (
    <Flex direction="column">
      <div role="alert" aria-live="assertive">
        {isFetchingIntakes && <SrOnly>Loading intakes table</SrOnly>}
      </div>
      <Table
        data={intakes}
        loading={isFetchingIntakes}
        overflow="scroll"
        columns={[
          {
            accessorKey: 'id',
            header: 'Select',
            cell: (intake: Intake) => (
              <Table.TextCell>
                <Checkbox
                  value={selectedIntakes.includes(intake.id)}
                  onChange={() => toggle(intake.id)}
                  aria-label={`Select ${intake.programName}`}
                />
              </Table.TextCell>
            ),
          },
          {
            accessorKey: 'programName',
            header: 'Program Name',
            cell: (intake: Intake) => <Table.TextCell>{intake.programName}</Table.TextCell>,
          },
          {
            accessorKey: 'intake',
            header: 'Intake',
            cell: (intake: Intake) => <Table.TextCell>{intake.intakeTermName}</Table.TextCell>,
          },
          {
            accessorKey: 'campus',
            header: 'Campus',
            cell: (intake: Intake) => <Table.TextCell>{intake.campusName}</Table.TextCell>,
          },
          {
            accessorKey: 'status',
            header: 'Status',
            cell: (intake: Intake) => (
              <Table.TextCell>
                <Tag intent={getIntakeStatusIntent(intake.status)}>{getIntakeStatus(intake.status)}</Tag>
              </Table.TextCell>
            ),
          },
          {
            accessorKey: 'link',
            header: () => <SrOnly>Program Intake Link</SrOnly>,
            cell: (intake: Intake) => {
              return (
                <ButtonLink
                  aria-label={`Program Intake ${intake.programName} Link`}
                  emphasis="transparent"
                  href={`/intake-management/${intake.id}`}
                  intent="secondary"
                  leadIcon={EyeOutlineIcon}
                  size="sm"
                  width="hug"
                />
              );
            },
          },
        ]}
      >
        <Table.NoData>
          <Flex align="center" justify="center" direction="column" gap={4} py={32}>
            <DocumentAlertHifiIcon />
            <Heading level={3}>No program intakes found.</Heading>
          </Flex>
        </Table.NoData>
      </Table>
    </Flex>
  );
}

function getIntakeStatus(intakeStatus: string): string {
  return enumIntakeStatuses.find((intake) => intake.code === intakeStatus)?.name as string;
}

function getIntakeStatusIntent(intakeStatus: string): 'positive' | 'negative' {
  return intakeStatus === 'OPEN' ? 'positive' : 'negative';
}
